// Real WebSocket Service for WordRush Multiplayer Backend
import { io, Socket } from 'socket.io-client';
import { Player, GameSettings } from '../context/GameContext';

/**
 * A joinable public game, exactly as the server sends it — the shape is
 * identical on `GET /api/rooms/public` and on the `public_rooms_updated`
 * broadcast, because both are built by `buildPublicRoomsSnapshot()`.
 *
 * Not to be confused with `GET /api/games/public`, which is FINISHED game
 * records. Seeding a joinable list from that endpoint would offer people games
 * that are already over.
 */
export interface PublicRoom {
  roomCode: string;
  hostName: string;
  hostAvatar: string | null;
  playerCount: number;
  maxPlayers: number;
  gameMode: string;
  status: 'waiting' | 'lobby' | 'playing';
}

/* ------------------------------------------------------------------ */
/* Errors — typed, not string-matched                                  */
/* ------------------------------------------------------------------ */

/**
 * The server's `error` channel is a bare `{ message }`. The client used to
 * decide "is this room-not-found or room-full?" by substring-matching that
 * English message — which breaks the moment anyone rewords a log line.
 *
 * We normalise every inbound error into a `WsError` with a stable `code`
 * exactly once, here. Callers switch on the code. If/when the backend
 * starts sending `code` itself, `normalizeWsError` prefers it and the
 * pattern table becomes dead weight we can delete.
 */
export type WsErrorCode =
  | 'room_not_found'
  | 'room_full'
  | 'game_in_progress'
  | 'not_authenticated'
  | 'not_host'
  | 'connection_failed'
  | 'unknown';

export interface WsError {
  code: WsErrorCode;
  message: string;
  /** The raw payload, for logging. */
  raw?: unknown;
}

const ERROR_PATTERNS: Array<[RegExp, WsErrorCode]> = [
  [/room\s*not\s*found|no\s*such\s*room|invalid\s*room\s*code/i, 'room_not_found'],
  [/room\s*is\s*full|game\s*is\s*full|max(imum)?\s*players/i, 'room_full'],
  [/already\s*(in\s*progress|started)|game\s*in\s*progress/i, 'game_in_progress'],
  [/not\s*authenticated|authenticate\s*first|please\s*sign\s*in/i, 'not_authenticated'],
  [/only\s*the\s*host|host\s*only|not\s*the\s*host/i, 'not_host'],
];

const KNOWN_CODES = new Set<WsErrorCode>([
  'room_not_found',
  'room_full',
  'game_in_progress',
  'not_authenticated',
  'not_host',
  'connection_failed',
  'unknown',
]);

export function normalizeWsError(raw: any): WsError {
  const message: string =
    (typeof raw === 'string' ? raw : raw?.message) || 'Something went wrong.';

  // 1. Server-supplied code always wins.
  const supplied = raw?.code;
  if (typeof supplied === 'string' && KNOWN_CODES.has(supplied as WsErrorCode)) {
    return { code: supplied as WsErrorCode, message, raw };
  }

  // 2. Fall back to the pattern table.
  for (const [pattern, code] of ERROR_PATTERNS) {
    if (pattern.test(message)) return { code, message, raw };
  }

  return { code: 'unknown', message, raw };
}

/* ------------------------------------------------------------------ */
/* Event contract                                                      */
/* ------------------------------------------------------------------ */

export interface AnswerCell {
  answer: string;
  /**
   * 0 / 5 / 10 normally. NEGATIVE (-5) when the `strictScoring` house rule is
   * on and the word was rejected, and anything the host picks after an
   * override — hence a plain number rather than a union.
   */
  points: number;
  /**
   * `override` — the host settled this one by hand; it is no longer "wrong".
   * `banned`   — a real word, off the board this round (Ladder rounds 3+). NOT a
   *              mistake, and it must never be rendered as one: the player who
   *              wrote it knew the word, they just weren't allowed it tonight.
   */
  status: 'blank' | 'invalid' | 'duplicate' | 'unique' | 'override' | 'banned';
  duplicateOf: string[];
  /** Optional human-readable reason for an invalid answer. */
  reason?: string;
  /** The host settled this cell by hand. */
  overridden?: boolean;
  overriddenBy?: string;
  previousPoints?: number;
  previousStatus?: string;
}

/** One word, off the board this round. No counts — a ban is a rule, not a claim. */
export interface BanWord {
  answer: string;
  display: string;
}
export type BanList = Record<string, BanWord[]>;

/**
 * THE RUNG YOU ARE STANDING ON.
 *
 * A Ladder run escalates Atom every round and you have one life. `verdict` is
 * null while the round is live and settled the instant the tally lands — which is
 * where the player finds out whether they are still alive.
 */
export interface LadderRung {
  round: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'insane';
  note?: string;
  verdict?: 'advance' | 'eliminated' | null;
  you?: number;
  atom?: number;
  /** Your best depth ever, as of the moment this run started. Only on round_started. */
  bestDepth?: number;
}

/** The run, over. Two numbers and the story of how you got there. */
export interface LadderRun {
  depth: number;
  cleared: number;
  clearedDifficulties: string[];
  eliminated: boolean;
  perfect: boolean;
  bestDepth: number;
  previousBest: number;
  isNewBest: boolean;
  rungs: Array<{
    round: number;
    letter: string;
    difficulty: string;
    you: number;
    atom: number;
    verdict: 'advance' | 'eliminated';
    bans: string[];
  }>;
}

export interface RoundEndedPayload {
  roundNumber: number;
  letter: string;
  scores: Record<string, number>;
  playerTotalScores: Record<string, number>;
  submissions: Record<string, Record<string, string>>;
  answerDetails: Record<string, Record<string, AnswerCell>>;
  bans?: BanList | null;
  ladder?: LadderRung | null;
  /**
   * This tally is a REPLAY of a round that scored while you were away — the
   * async case, where the person who ends the round is by definition not the
   * person who left it. Same payload, same cascade; the client just doesn't
   * pretend the round ended a moment ago, and acks it with `tallySeen`.
   */
  isReplay?: boolean;
}

export interface GameStateRestorePayload {
  active: boolean;
  roomCode?: string;
  isHost?: boolean;
  status?: 'waiting' | 'lobby' | 'playing' | 'completed' | string;
  currentRound?: number;
  totalRounds?: number;
  currentLetter?: string;
  timeRemaining?: number;
  categories?: string[];
  players?: Player[];
  gameSettings?: Partial<GameSettings>;

  // --- Async / correspondence resume ------------------------------------
  // Coming back to an async game HOURS later is the normal case, not the edge
  // case, so the resume payload has to carry the whole answer: whose turn it is,
  // how long they have, and the tally they slept through.
  isAsync?: boolean;
  yourTurn?: boolean;
  waitingOn?: string[];
  turnDeadlineMs?: number | null;
  /** A completed round whose tally this player never saw. Replayed into the cascade. */
  pendingTally?: RoundEndedPayload | null;
}

/**
 * The typed contract. **Everything the service `emit`s must appear here** —
 * the previous version emitted seven events (`gameEnded`, `tvSnapshot`,
 * `players_updated`, `timer_started`, …) that were absent from this
 * interface, so the runtime contract and the typed contract had silently
 * drifted apart and `tsc` flagged each call site instead of the gap.
 */
/** Who is watching this room from the wall. */
export interface ObserverState {
  roomCode: string;
  observers: number;
  tvConnected: boolean;
}

/**
 * THE BROADCAST PACKAGE — a television's cold start.
 *
 * `phase` is server-derived, because the server is the only thing that knows
 * whether a room is mid-race or sitting in the tally window between rounds.
 *
 * Note what a race-phase snapshot does NOT carry: no submissions, no drafts,
 * no answer text. `lastRound` only exists once a round has CLOSED — at which
 * point every phone in the room can see the words anyway.
 */
export interface TvSnapshot {
  roomCode: string;
  status: 'waiting' | 'lobby' | 'playing' | 'completed';
  phase: 'lobby' | 'race' | 'tally' | 'final';
  currentRound: number;
  totalRounds: number;
  currentLetter: string | null;
  timeRemaining: number;
  timeLimit: number;
  categories: string[];
  players: Player[];
  submittedIds: string[];
  gameMode: string;
  observers: number;
  lastRound: RoundEndedPayload | null;
  finalResults: any | null;
  serverTime: number;
}

/** One thing Atom said, and when it said it. */
export interface AtomSaysPayload {
  text: string;
  occasion: 'round_start' | 'squeeze' | 'tally' | 'game_end';
  kind?: 'standard' | 'celebration' | 'urgent';
  /** Which of the four voices — follows from the difficulty you chose. */
  persona: 'encouraging' | 'friendly' | 'witty' | 'competitive';
  difficulty: string;
  botId: string;
  roundNumber: number;
  /** `atom` = the deterministic voice; `llm` = the model got past the screen.
   *  Diagnostic only — a player must never be able to tell the difference. */
  source: 'atom' | 'llm';
}

export interface WebSocketEvents {
  // Connection events
  connect: void;
  disconnect: void;
  /** Socket.io gave up (all reconnect attempts exhausted). Terminal. */
  connection_lost: { attempts: number };
  /** Socket came back after a drop; auth + room re-join already replayed. */
  reconnected: void;
  error: WsError;

  // Authentication events
  authenticated: { userId: string; username: string; avatar: string };
  authError: { message: string };

  // Room events
  room_created: { roomCode: string; hostId: string; isHost: boolean; gameSettings: GameSettings; players?: Player[] };
  room_joined: { roomCode: string; players: Player[]; isHost: boolean };
  room_left: { playerId: string };
  room_not_found: { roomCode: string };
  room_full: { roomCode: string };

  // Player events - Legacy names (for compatibility)
  player_joined: { player: Player };
  player_left: { playerId: string };
  player_ready: { playerId: string; isReady: boolean };
  player_updated: { player: Player };

  // Player events - Backend names (real events from server)
  playerJoined: { player?: Player; players?: Player[] };
  playerLeft: { userId: string; players?: Player[]; gameState?: any };
  playerReadyChanged: { userId: string; isReady: boolean; gameState?: any };
  player_typing: { playerId: string; category?: string };
  player_stopped_typing: { playerId: string };

  // Roster / presence
  players_updated: { players: Player[] };
  public_rooms_updated: { rooms: PublicRoom[] };
  friendsPresence: { online: string[] };
  myActiveGames: { games: any[] };

  /** Host changed the room's rules or its visibility. Broadcast to EVERYONE in
   *  the lobby, not just the host — a player has a right to know that the room
   *  they are sitting in has just been opened to strangers. */
  gameSettingsUpdated: { gameSettings: GameSettings };

  // Quick Match. Exactly three outcomes and the UI must handle all three:
  // we seated you, the pool was empty (so make your own), or you may not.
  quickMatchFound: { roomCode: string; players: number };
  quickMatchEmpty: { shouldCreate?: boolean; message?: string };
  quickMatchBlocked: { code?: string; message?: string };
  /** Refused entry to a stranger-visible room — not signed in, or a block. */
  joinBlocked: { roomCode: string; code?: string; message?: string };
  /** The server dealt you a fresh guest name. See `rerollName()`. */
  nameChanged: { userId: string; name: string };
  /**
   * The server unlocked something at the end of a game. It has pushed this for
   * as long as achievements have existed and the client never declared it, so
   * it fell on the floor — you could only find out you had earned a badge by
   * navigating to the page and reloading it.
   */
  achievementsUnlocked: {
    achievements: Array<{ key: string; name: string; icon: string }>;
  };

  // Game state synchronization
  gameState: any;
  /** Server-authoritative rehydration after refresh / reconnect. */
  gameStateRestore: GameStateRestorePayload;
  /**
   * Sent on SIGTERM — i.e. on every deploy — right before the server flushes
   * room state to Firestore and exits. The game is NOT lost; the next revision
   * rehydrates it and socket.io reconnects us. Surface it so a deploy reads as
   * "one moment" rather than "the app is broken".
   */
  server_restarting: { reason?: string };
  hostTransferred: { newHostId: string; newHostName: string; gameState?: any };

  // Kick events
  kicked: { message: string; roomCode: string };
  playerKicked: { kickedPlayerId: string; kickedPlayerName: string; players: Player[] };

  // Rematch event
  rematchStarted: { roomCode: string; players: Player[]; gameSettings: GameSettings };

  // Game events
  game_starting: { settings: GameSettings; countdown: number };
  game_started: { gameId?: string; totalRounds?: number };
  round_started: {
    roundNumber: number;
    letter: string;
    timeLimit: number;
    categories: string[];
    /** The round opens with the letter AND the ban, together. Never after. */
    bans?: BanList | null;
    ladder?: LadderRung | null;
  };
  round_ended: RoundEndedPayload;
  game_ended: any;
  /** Alias of `game_ended` — the TV canvas listens on this name. */
  gameEnded: any;
  /**
   * The host aborted before the game really started. No result, no loss —
   * everyone goes home. Not the same as a player forfeiting mid-game.
   */
  game_aborted: { byName?: string; reason?: string };

  // Gameplay events
  answer_submitted: { playerId: string; roundNumber: number };
  time_warning: { timeRemaining: number };
  timer_started: { firstPlayer?: string; timeRemaining?: number };
  timer_update: { timeRemaining: number };
  /**
   * The squeeze: the first player hit Done, so everyone else gets a hard
   * cap (`squeezeSeconds`, default 10). Emitted by the backend at first submit.
   * It can only ever SHORTEN the clock — the server clamps to
   * `min(endsAt, now + seconds)`.
   */
  time_squeeze: { byName: string; secondsLeft: number };

  /**
   * The host settled a disputed word. Broadcast to EVERY client so the chip
   * and the running totals re-flip on all phones, not just the host's.
   */
  score_adjusted: {
    playerId: string;
    category: string;
    points: number;
    roundNumber?: number;
    /** The rescored cell, authoritative. Comes back with status 'override'. */
    cell?: AnswerCell;
    /** Per-round score map, if the server recomputed it. */
    scores?: Record<string, number>;
    /** Running totals, if the server recomputed them. */
    playerTotalScores?: Record<string, number>;
    /**
     * ✓ Mor'du has been added to MOVIE NIGHT.
     *
     * Set when the override taught the pack a word it did not have — which is the
     * whole custom-games feature, delivered by a chip that already existed. Null
     * for most overrides (settling a misspelling teaches nothing), and null in
     * Classic, which has no key to teach.
     */
    learned?: { word: string; column: string; packId: string; packName: string } | null;
  };

  /**
   * The game record has been written and its share card is renderable.
   *
   * Arrives a beat AFTER `game_ended`, because the record id is
   * `${roomCode}_${endedAtMs}` and `endedAtMs` is minted server-side during the
   * save. The results screen shows immediately on `game_ended`; the card slides
   * in under it when this lands — which is the memo's whole point about the
   * final-results bridge.
   */
  shareReady: { gameId: string };

  // TV / observer mode
  tvSnapshot: TvSnapshot;

  /**
   * A television is watching this room — or has stopped watching it.
   *
   * ONE event, two consumers. The phones flip into CONTROLLER mode (the TV
   * owns the drama now; the phone is four inputs and a button). The sound
   * layer ducks, because two speakers playing the same clink 40ms apart is
   * worse than either speaker alone.
   */
  observer_joined: ObserverState;
  observer_left: ObserverState;

  /**
   * ATOM SAID SOMETHING ABOUT YOUR GAME.
   *
   * Derived server-side from the round that actually happened — the collisions,
   * the word nobody else got, the column the whole table blanked, the gap. It is
   * true by construction: the server builds every line from the same scored
   * round the tally is rendering from, and screens any generated line that
   * contains a number the round didn't produce (see `atomVoice.screen`).
   *
   * It may never arrive — no API key, spent budget, a slow model — and that is a
   * normal, expected, silent outcome. See <AtomSays>.
   */
  atom_says: AtomSaysPayload;

  // Chat events
  message_sent: { playerId: string; message: string; timestamp: number };
  message_received: { playerId: string; playerName: string; message: string; timestamp: number };
  messageReceived: { id: string; playerId: string; playerName: string; playerAvatar: string; message: string; timestamp: number; roomCode: string };
}

/**
 * Server events that are forwarded to local subscribers *verbatim* — same
 * name, same payload, no reshaping.
 *
 * This list exists because the old hand-rolled forwarders silently dropped
 * events: `gameStateRestore`, `public_rooms_updated`, `friendsPresence` and
 * `myActiveGames` were all *subscribed to* somewhere in the app and were
 * *never registered on the socket*, so those code paths were dead. A single
 * declarative list makes that class of bug impossible to reintroduce.
 */
const PASSTHROUGH_EVENTS = [
  'gameState',
  'gameStateRestore',
  // The server emits this on SIGTERM (i.e. every Cloud Run deploy) just before
  // it flushes room state and exits. Without it the UI freezes silently for the
  // few seconds it takes the new revision to boot, which reads as "the game
  // broke". The room IS being persisted and will come back — say so.
  'server_restarting',
  'players_updated',
  'public_rooms_updated',
  // The server has always broadcast this to the whole lobby on every host
  // settings change, and no client has ever listened. So the house rules only
  // ever updated on the host's own screen (via an optimistic local dispatch)
  // and every other player sat looking at stale rules. It matters more now:
  // "anyone can join" is a settings change, and a player has a right to see
  // the moment their room is opened to strangers.
  'gameSettingsUpdated',
  // Quick Match's three outcomes, and the refusal a stranger-visible room can
  // hand back on join. All four were emitted by the server and heard by nobody.
  'quickMatchFound',
  'quickMatchEmpty',
  'quickMatchBlocked',
  'joinBlocked',
  'achievementsUnlocked',
  'friendsPresence',
  'myActiveGames',
  'tvSnapshot',
  // The room's TV came on / went off. Every phone in the room needs this: it
  // is what turns a player's screen into a controller and back again.
  'observer_joined',
  'observer_left',
  'timer_started',
  'timer_update',
  'time_squeeze',
  'score_adjusted',
  // Fired the moment the gameRecord is written, carrying the id the share card
  // lives at. The client cannot derive it: the record id is
  // `${roomCode}_${endedAtMs}` and endedAtMs is minted server-side a beat AFTER
  // `gameEnded` is broadcast. Without this the results screen would have to poll
  // or guess — and the memo is unambiguous that any friction between the laugh
  // and the send kills the send.
  'shareReady',
  'room_not_found',
  'room_full',
  // `room_joined` is forwarded verbatim (the server already sends
  // client-shaped players) so a RECONNECT re-join — which happens outside
  // any `joinRoom()` call — still refreshes the roster.
  // `room_created` is deliberately NOT here: it can only ever arrive in
  // response to our own `createRoom()`, which emits the mapped version.
  'room_joined',
  'messageReceived',
  'kicked',
  'playerKicked',
  'hostTransferred',
  'rematchStarted',
  'playerReadyChanged',
  'player_typing',
  'player_stopped_typing',
  // Atom, talking about the round that just happened. Verbatim — the server has
  // already decided what is true and what is sayable; the client just renders it.
  'atom_says',
] as const;

interface AuthCredentials {
  username: string;
  avatar: string;
  firebaseUserId?: string;
  firebaseToken?: string;
}

const RECONNECT_ATTEMPTS = 5;

// Real WebSocket implementation connecting to Node.js backend
class RealWebSocketService {
  private socket: Socket | null = null;
  private listeners: Partial<Record<keyof WebSocketEvents, Function[]>> = {};
  private isConnected = false;
  private currentRoom: string | null = null;
  /** The room this client is WATCHING (TV canvas / cast receiver). Never played. */
  private observerRoom: string | null = null;
  private currentUser: Player | null = null;
  private connectionPromise: Promise<void> | null = null;
  /** Guards against re-registering every socket handler on each reconnect. */
  private handlersBound = false;
  /** True once we've seen at least one successful `connect`. */
  private hasConnectedOnce = false;
  /**
   * Supplies fresh credentials on reconnect. Set by GameContext — it owns
   * the Firebase user and can mint a *fresh* ID token (the one we
   * authenticated with may have expired while we were offline).
   */
  private authProvider: (() => Promise<AuthCredentials>) | null = null;

  constructor() {
    const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL || `http://${window.location.hostname}:8004`;
    console.log('🚀 WebSocket service target:', websocketUrl);
  }

  // Event listener management
  on<K extends keyof WebSocketEvents>(event: K, callback: (data: WebSocketEvents[K]) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event]!.push(callback);
  }

  off<K extends keyof WebSocketEvents>(event: K, callback: (data: WebSocketEvents[K]) => void) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event]!.filter(cb => cb !== callback);
    }
  }

  private emit<K extends keyof WebSocketEvents>(event: K, data?: WebSocketEvents[K]) {
    const subs = this.listeners[event];
    if (!subs || subs.length === 0) return;
    // Copy before iterating — a handler that calls off() mid-dispatch
    // would otherwise skip the next subscriber.
    [...subs].forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error(`Listener for "${String(event)}" threw:`, e);
      }
    });
  }

  /**
   * Register the credential source used to re-authenticate after a socket
   * reconnect. Without this, a dropped socket comes back *anonymous*: the
   * server no longer knows who we are, so every subsequent emit is silently
   * rejected and the player sits in a dead game.
   */
  setAuthProvider(fn: (() => Promise<AuthCredentials>) | null) {
    this.authProvider = fn;
  }

  // Connection management
  async connect(): Promise<void> {
    if (this.socket?.connected) return;
    if (this.connectionPromise) {
      console.log('🔌 Connection already in progress, waiting...');
      return this.connectionPromise;
    }

    // NOTE: the previous implementation cleared `connectionPromise` in a
    // `finally` block *inside* the executor, which runs synchronously —
    // so the de-dupe never actually de-duped. Clear it on settle instead.
    const p = this._connect();
    this.connectionPromise = p;
    p.catch(() => {}).finally(() => {
      if (this.connectionPromise === p) this.connectionPromise = null;
    });
    return p;
  }

  private async _connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL || `http://${window.location.hostname}:8004`;
        console.log(`🔌 Connecting to WebSocket server at: ${websocketUrl}`);

        this.socket = io(websocketUrl, {
          // WebSocket FIRST. Polling-first means the handshake is a series
          // of separate XHRs, and behind a multi-instance load balancer
          // those can land on different server instances — which is exactly
          // how you get the intermittent `TransportError: xhr poll error`
          // / 400 responses we see in production. Polling stays in the list
          // purely as a fallback for networks that block ws://.
          transports: ['websocket', 'polling'],
          timeout: 30000,
          forceNew: true,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: RECONNECT_ATTEMPTS,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          upgrade: true,
          rememberUpgrade: true,
        });

        let settled = false;

        this.socket.on('connect', () => {
          this.isConnected = true;
          console.log(`✅ Connected to WordRush backend (socket ${this.socket?.id})`);
          // Bind ONCE. socket.io reuses the same Socket instance across
          // reconnects and keeps its listeners, so re-running this on every
          // `connect` used to duplicate every handler — meaning after one
          // network blip each server event fired twice, after two blips
          // three times, and so on.
          if (!this.handlersBound) {
            this.setupEventHandlers();
            this.handlersBound = true;
          }

          const isReconnect = this.hasConnectedOnce;
          this.hasConnectedOnce = true;

          if (!settled) {
            settled = true;
            this.emit('connect');
            resolve();
          } else if (isReconnect) {
            this.emit('connect');
            void this.replaySession();
          }
        });

        this.socket.on('connect_error', (error: any) => {
          console.error('❌ WebSocket connection failed:', error?.message || error);
          this.emit('error', {
            code: 'connection_failed',
            message: 'Failed to connect to server',
            raw: error,
          });
          // Only fail the initial connect() promise. Subsequent
          // connect_errors are reconnect attempts — socket.io keeps
          // retrying and `reconnect_failed` is the terminal signal.
          if (!settled) {
            settled = true;
            reject(error);
          }
        });

        this.socket.on('disconnect', (reason: string) => {
          this.isConnected = false;
          console.log('🔌 Disconnected from server:', reason);
          this.emit('disconnect');
        });

        // Terminal failure. Socket.io exhausted its reconnect budget and
        // will NEVER try again — previously this went completely unhandled,
        // so the app just sat there looking alive while being permanently
        // offline. Surface it: the EdgeState screen for this already exists.
        this.socket.io.on('reconnect_failed', () => {
          console.error('❌ Reconnect failed — giving up after', RECONNECT_ATTEMPTS, 'attempts');
          this.isConnected = false;
          this.emit('connection_lost', { attempts: RECONNECT_ATTEMPTS });
          this.navigateTo('/error?type=connection-lost');
        });

        this.socket.io.on('reconnect', (attempt: number) => {
          console.log(`🔁 Reconnected after ${attempt} attempt(s)`);
        });
      } catch (error) {
        console.error('❌ Connection setup failed:', error);
        reject(error);
      }
    });
  }

  /**
   * Ask App.tsx to route somewhere. The service lives outside React, so it
   * can't call `useNavigate` — App.tsx listens for this DOM event.
   */
  private navigateTo(to: string) {
    try {
      window.dispatchEvent(new CustomEvent('wr:navigate', { detail: { to, replace: true } }));
    } catch {}
  }

  /**
   * After a reconnect the socket is a *stranger* to the server: new socket
   * id, no user binding, not in any socket.io room. Replay the session:
   * re-authenticate (with a freshly-minted token), re-join the room, and
   * ask for the authoritative game state.
   */
  private async replaySession(): Promise<void> {
    try {
      if (this.authProvider) {
        const creds = await this.authProvider();
        await this.authenticate(creds.username, creds.avatar, creds.firebaseUserId, creds.firebaseToken);
        console.log('🔐 Re-authenticated after reconnect');
      }
      if (this.currentRoom && this.socket) {
        console.log('🚪 Re-joining room after reconnect:', this.currentRoom);
        this.socket.emit('joinRoom', this.currentRoom);
      }
      // A television is a client too, and it is the ONE client with nobody
      // watching it closely enough to notice it has gone quiet. Re-observe and
      // re-snapshot — never `joinRoom`, which would seat the TV as a player.
      if (this.observerRoom && this.socket) {
        console.log('📺 Re-observing room after reconnect:', this.observerRoom);
        this.socket.emit('joinAsObserver', { roomCode: this.observerRoom });
      }
      this.socket?.emit('getGameState');
      this.emit('reconnected');
    } catch (e) {
      console.error('❌ Session replay after reconnect failed:', e);
      this.emit('error', normalizeWsError({ message: 'Lost your seat — please rejoin.' }));
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.handlersBound = false;
    this.hasConnectedOnce = false;
    this.isConnected = false;
    this.currentRoom = null;
    this.currentUser = null;
    this.emit('disconnect');
  }

  getConnectionStatus(): 'connected' | 'disconnected' | 'connecting' | 'error' {
    if (!this.socket) return 'disconnected';
    if (this.socket.connected && this.isConnected) return 'connected';
    if (!this.socket.connected && this.isConnected) return 'connecting';
    return 'disconnected';
  }

  // Authentication
  async authenticate(username: string, avatar: string, firebaseUserId?: string, firebaseToken?: string): Promise<Player> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      console.log(`🔐 Authenticating user: ${username}`);

      const onOk = (data: any) => {
        cleanup();
        this.currentUser = {
          id: data.userId,
          name: data.username,
          avatar: data.avatar,
          isReady: false,
          isHost: false,
          score: 0,
        };
        this.emit('authenticated', data);
        resolve(this.currentUser);
      };
      const onErr = (error: any) => {
        cleanup();
        console.error('🚫 Authentication failed:', error);
        this.emit('authError', { message: error?.message || 'Authentication failed' });
        reject(new Error(error?.message || 'Authentication failed'));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('authenticated', onOk);
        socket.off('authError', onErr);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Authentication timeout'));
      }, 15000);

      socket.on('authenticated', onOk);
      socket.on('authError', onErr);
      socket.emit('authenticate', { username, avatar, firebaseUserId, firebaseToken });
    });
  }

  // Room management
  async createRoom(_player: Player, settings?: Record<string, any>): Promise<string> {
    return new Promise(async (resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      console.log('🎮 Creating room with settings:', settings);

      // Race-tolerant: if the user clicked "Start" before the socket's
      // `authenticated` event landed, wait up to 4s for it.
      if (!this.currentUser) {
        try {
          await new Promise<void>((authResolve, authReject) => {
            const t = setTimeout(() => authReject(new Error('auth wait timeout')), 4000);
            const handler = () => {
              if (this.currentUser) {
                clearTimeout(t);
                this.off('authenticated', handler as any);
                authResolve();
              }
            };
            this.on('authenticated', handler as any);
            if (this.currentUser) handler();
          });
        } catch {
          reject(new Error('Not authenticated. Please refresh and try again.'));
          return;
        }
      }

      const onCreated = (data: any) => {
        cleanup();
        this.currentRoom = data.roomCode;
        console.log('✅ Room created:', data.roomCode);
        this.emit('room_created', {
          roomCode: data.roomCode,
          hostId: this.currentUser!.id,
          isHost: data.isHost,
          gameSettings: data.gameSettings,
          players: data.players || [],
        });
        resolve(data.roomCode);
      };
      const onError = (raw: any) => {
        cleanup();
        const err = normalizeWsError(raw);
        console.error('❌ Room creation failed:', err);
        this.emit('error', err);
        reject(new Error(err.message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('room_created', onCreated);
        socket.off('error', onError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Create room timeout'));
      }, 10000);

      socket.once('room_created', onCreated);
      socket.once('error', onError);
      socket.emit('createRoom', settings || {});
    });
  }

  async joinRoom(roomCode: string, _player: Player): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }
      if (!this.currentUser) {
        reject(new Error('Not authenticated. Please authenticate first.'));
        return;
      }

      const toPlayers = (list: any[]): Player[] =>
        list.map((p: any) => ({
          id: p.id || p.userId,
          name: p.name || p.username,
          avatar: p.avatar,
          isReady: p.isReady || false,
          isHost: p.isHost || false,
          isBot: p.isBot || false,
          isConnected: p.isConnected !== false,
          score: p.totalScore || p.score || 0,
        }));

      // Delivery to subscribers is handled by the PASSTHROUGH forward — we
      // only need to settle the promise and remember the room. Re-emitting
      // here would double-dispatch `room_joined` to every listener.
      const onJoined = (data: any) => {
        cleanup();
        this.currentRoom = data?.roomCode || roomCode;
        resolve(true);
      };

      const onGameState = (gameState: any) => {
        if (!Array.isArray(gameState?.players)) return;
        cleanup();
        this.currentRoom = roomCode;
        const players = toPlayers(gameState.players);
        this.emit('room_joined', {
          roomCode,
          players,
          isHost: players.find(p => p.id === this.currentUser!.id)?.isHost || false,
        });
        resolve(true);
      };

      // Switch on a normalised CODE, not on the English message text.
      const onError = (raw: any) => {
        cleanup();
        const err = normalizeWsError(raw);
        console.error('❌ joinRoom error:', err);
        switch (err.code) {
          case 'room_not_found':
            this.emit('room_not_found', { roomCode });
            break;
          case 'room_full':
          case 'game_in_progress':
            this.emit('room_full', { roomCode });
            break;
          default:
            this.emit('error', err);
        }
        resolve(false);
      };

      /**
       * THE DOOR SAID NO.
       *
       * `joinBlocked` is the server's refusal — you are blocked by someone in
       * that room, or the room is stranger-visible and you have no account. It
       * is NOT an `error`, so nothing here settled the promise: the join sat in
       * silence for ten seconds and then died as "Join room timeout".
       *
       * So the enforcement worked perfectly and the person on the other end was
       * told, in effect, that the internet was broken. Forward the real reason.
       */
      const onBlocked = (data: any) => {
        cleanup();
        this.emit('joinBlocked', {
          roomCode: data?.roomCode || roomCode,
          code: data?.code,
          message: data?.message,
        });
        resolve(false);
      };

      const cleanup = () => {
        clearTimeout(timer);
        socket.off('room_joined', onJoined);
        socket.off('gameState', onGameState);
        socket.off('error', onError);
        socket.off('joinBlocked', onBlocked);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Join room timeout'));
      }, 10000);

      socket.once('room_joined', onJoined);
      socket.once('gameState', onGameState);
      socket.once('error', onError);
      socket.once('joinBlocked', onBlocked);
      socket.emit('joinRoom', roomCode);
    });
  }

  async leaveRoom(playerId: string): Promise<void> {
    if (this.socket) {
      this.socket.emit('leaveRoom');
      // Null FIRST, synchronously. `inRoom()` below reads this to drop any
      // room event that lands after we've walked out.
      this.currentRoom = null;
      this.emit('room_left', { playerId });
    }
  }

  /**
   * Are we still in a room? Gameplay events that arrive after we've left must
   * be dropped on the floor.
   *
   * Leaving a solo game made the server end it and emit `gameEnded` — at which
   * point the client dutifully navigated the departing player to /game-results.
   * You tapped "go home", and the app took you to a results screen for a game
   * you had just quit. Same shape as the original trap, one door further along.
   */
  /**
   * "Are these broadcasts for me?"
   *
   * True for a PLAYER in a room — and also for a TELEVISION observing one.
   * Without the second clause every mapped gameplay event (`round_started`,
   * `round_ended`, `game_ended`) was dropped on the floor for observers,
   * which is precisely why the TV canvas used to get a single snapshot on
   * join and then nothing for the rest of the game. It was a screenshot, not
   * a broadcast.
   */
  private inRoom(): boolean {
    return this.currentRoom !== null || this.observerRoom !== null;
  }

  /* ---------------- TV / observer mode ----------------
   * An observer takes no seat, sends no input, and — critically — is NEVER
   * re-joined with `joinRoom` on reconnect. It is a third kind of client.
   */

  /** Join a room as a passive observer (the TV canvas / the cast receiver). */
  joinAsObserver(roomCode: string): void {
    const code = (roomCode || '').toUpperCase();
    if (!code) return;
    this.observerRoom = code;
    this.socket?.emit('joinAsObserver', { roomCode: code });
  }

  leaveAsObserver(): void {
    if (!this.observerRoom) return;
    this.socket?.emit('leaveAsObserver');
    this.observerRoom = null;
  }

  /** "Am I still showing the right thing?" — the TV's slow heartbeat. */
  requestTvResync(): void {
    if (!this.observerRoom) return;
    this.socket?.emit('tvResync');
  }

  /** "Is there a television in this room?" — asked by phones, not TVs. */
  requestObserverState(roomCode: string): void {
    const code = (roomCode || '').toUpperCase();
    if (!code) return;
    this.socket?.emit('getObserverState', { roomCode: code });
  }

  // Player actions
  async setPlayerReady(playerId: string, isReady: boolean): Promise<void> {
    if (!this.socket) {
      console.error('❌ No socket connection for setPlayerReady');
      return;
    }
    this.socket.emit('setReady', isReady);
    this.emit('player_ready', { playerId, isReady });
  }

  // Game management
  async startGame(_settings?: GameSettings): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      console.log('🚀 Starting game...');

      // Resolve on the server's ack (roundStarted / gameStarted) rather than
      // optimistically — callers want to know the game *actually* began.
      const onStarted = () => {
        cleanup();
        resolve();
      };
      const onError = (raw: any) => {
        const err = normalizeWsError(raw);
        cleanup();
        this.emit('error', err);
        reject(new Error(err.message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('gameStarted', onStarted);
        socket.off('roundStarted', onStarted);
        socket.off('error', onError);
      };
      // Don't hang forever if the server never answers.
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Start game timeout'));
      }, 10000);

      socket.once('gameStarted', onStarted);
      socket.once('roundStarted', onStarted);
      socket.once('error', onError);
      socket.emit('startGame');
    });
  }

  /**
   * Dev-mode helper: ask the server to AI-fill a full answer set for the
   * current round. Never reachable in production — the only caller is behind
   * `import.meta.env.DEV`, so Vite tree-shakes the button away entirely.
   */
  async devAutofill(): Promise<Record<string, string>> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) return reject(new Error('Not connected'));
      const handle = (result: any) => {
        cleanup();
        if (result?.ok && result.answers) resolve(result.answers);
        else reject(new Error(result?.error || 'autofill failed'));
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('autofill timeout'));
      }, 12_000);
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('dev:autofill:result', handle);
      };
      socket.once('dev:autofill:result', handle);
      socket.emit('dev:autofill', {});
    });
  }

  async submitAnswers(answers: Record<string, string>): Promise<void> {
    if (this.socket && this.currentUser) {
      this.socket.emit('submitAnswers', answers);
    }
  }

  /**
   * Ship the player's IN-PROGRESS answers to the server so that a round which
   * ends on the clock — rather than on an explicit "I'M DONE" — still scores
   * what they had actually typed.
   *
   * Before this, running out of time scored you a row of blanks no matter how
   * much you'd written. That is the single worst thing a word game can do to
   * you, and every playtester hit it.
   *
   * The server stores this on the player and grades it at timeout. It is NEVER
   * broadcast — a draft is private until the round ends, or the game would leak
   * everyone's answers live.
   */
  sendDraftAnswers(answers: Record<string, string>): void {
    if (!this.socket || !this.currentUser) return;
    this.socket.emit('draft_answers', answers);
  }

  /**
   * Host-only: tear the game down before it has really begun. Distinct from
   * `leaveRoom` — an abort ends the game for EVERYONE with no result recorded,
   * which is what you want when you started by mistake or a player never showed.
   */
  abortGame(): void {
    this.socket?.emit('abortGame');
  }

  /**
   * THE HOST OVERRIDE. Host says the word counts (or doesn't). This is the
   * escape valve that makes strict validation tolerable: the wordlist is
   * allowed to be wrong, because the table gets the final say.
   *
   * It goes to the SERVER, which rescores and broadcasts `score_adjusted` to
   * everyone. An override that only moved the number on the host's own phone
   * would be a lie — it would look like the argument was settled while every
   * other player still saw the old score.
   */
  overrideScore(playerId: string, category: string, points: number): void {
    if (!this.socket) return;
    this.socket.emit('override_score', {
      roomCode: this.currentRoom,
      playerId,
      category,
      points,
    });
  }

  async sendMessage(_playerId: string, message: string): Promise<void> {
    if (this.socket && this.currentUser) {
      this.socket.emit('sendMessage', message);
    }
  }

  /**
   * Ask the server to add an AI bot to the host's room. Note that for SOLO
   * rooms the backend already spawns Atom during `createRoom` (it reads
   * `settings.difficulty`) — calling this as well is what produced the
   * infamous "two Atoms who duplicate each other" game. Callers must check
   * the roster first; see `SoloSetupScreen`.
   */
  async addBot(
    botType: 'atom' = 'atom',
    options?: { difficulty?: 'easy' | 'medium' | 'hard' | 'expert' | 'insane' },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }
      const difficulty = options?.difficulty;
      console.log('🤖 Adding bot:', botType, difficulty ? `(${difficulty})` : '');

      const handleJoined = () => {
        cleanup();
        resolve();
      };
      const handleError = (raw: any) => {
        cleanup();
        reject(new Error(normalizeWsError(raw).message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('playerJoined', handleJoined);
        socket.off('error', handleError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Add bot timeout'));
      }, 5000);

      socket.once('playerJoined', handleJoined);
      socket.once('error', handleError);
      socket.emit('addBot', { botType, difficulty });
    });
  }

  async kickPlayer(playerId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      const onKicked = () => {
        cleanup();
        resolve();
      };
      const onError = (raw: any) => {
        cleanup();
        reject(new Error(normalizeWsError(raw).message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('playerKicked', onKicked);
        socket.off('error', onError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Kick player timed out'));
      }, 5000);

      socket.once('playerKicked', onKicked);
      socket.once('error', onError);
      socket.emit('kickPlayer', playerId);
    });
  }

  /**
   * "Give me another one."
   *
   * A guest never chose their name — they were HANDED one ("Kind Otter") at the
   * door. The server has been able to deal them a fresh one since guest names
   * existed: it screens the new name, makes it unique against everyone else at
   * the table, tells the room, and re-checkpoints. Nothing ever asked it to.
   *
   * Resolves with the name they ended up with — which is NOT necessarily the one
   * the server first generated (two Kind Otters in one room is a de-dupe), so
   * the caller must use what comes back rather than guessing.
   */
  async rerollName(): Promise<string> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      const onChanged = (data: { name: string }) => {
        cleanup();
        resolve(data?.name || '');
      };
      const onError = (raw: any) => {
        cleanup();
        reject(new Error(normalizeWsError(raw).message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('nameChanged', onChanged);
        socket.off('error', onError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Name change timed out'));
      }, 5000);

      socket.once('nameChanged', onChanged);
      socket.once('error', onError);
      socket.emit('rerollName');
    });
  }

  /**
   * Hand the crown to another player. Previously the Lobby raw-emitted
   * `transferHost` straight onto the socket and swallowed every failure into
   * a `console.warn` — the user tapped "Make host", nothing happened, and no
   * one was told why.
   */
  async transferHost(newHostId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      const onTransferred = () => {
        cleanup();
        resolve();
      };
      const onError = (raw: any) => {
        cleanup();
        reject(new Error(normalizeWsError(raw).message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('hostTransferred', onTransferred);
        socket.off('error', onError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Transfer host timed out'));
      }, 5000);

      socket.once('hostTransferred', onTransferred);
      socket.once('error', onError);
      socket.emit('transferHost', newHostId);
    });
  }

  /** Host-only: skip the between-rounds wait and start the next round now. */
  nextRound(): void {
    this.socket?.emit('nextRound');
  }

  /**
   * Host-only: push changed lobby settings to the server.
   *
   * ⚠️ Until now the lobby's settings sheet only ever dispatched into local
   * React state — rounds, time limit, house rules, all of it — so NOTHING the
   * host changed after `createRoom` ever reached the server. The sheet was
   * decorative. This is the wire it was missing; it needs the matching
   * `updateSettings` handler on the backend to take effect.
   */
  updateGameSettings(settings: Record<string, any>): void {
    this.socket?.emit('updateSettings', settings);
  }

  /**
   * Ask to be seated in an open public game.
   *
   * Fire-and-forget on purpose: the server answers on one of three separate
   * channels (`quickMatchFound` / `quickMatchEmpty` / `quickMatchBlocked`) and
   * the caller listens for all three. Wrapping this in a promise would force us
   * to pick ONE of them as "the" resolution and quietly swallow the other two —
   * and "we couldn't seat you, here's why" is not an error to be swallowed. It
   * is the answer.
   */
  quickMatch(): void {
    this.socket?.emit('quickMatch');
  }

  /** Ask the server for the authoritative state of whatever room we're in. */
  requestGameState(): void {
    this.socket?.emit('getGameState');
  }

  /**
   * "I watched round N's tally." Acks a replayed async cascade so the server
   * doesn't hand us the same collisions again on the next refresh.
   *
   * Fire-and-forget: the worst case of a lost ack is seeing a tally you already
   * saw, which is a great deal better than never seeing one you didn't.
   */
  tallySeen(roundNumber: number): void {
    this.socket?.emit('tallySeen', { roundNumber });
  }

  // Setup real-time event handlers from backend
  private setupEventHandlers(): void {
    const socket = this.socket;
    if (!socket) return;

    // ---- Verbatim forwards -------------------------------------------
    for (const name of PASSTHROUGH_EVENTS) {
      socket.on(name, (data: any) => {
        this.emit(name as keyof WebSocketEvents, data);
      });
    }

    // ---- Reshaped / aliased forwards ---------------------------------

    /**
     * THE TALLY YOU SLEPT THROUGH.
     *
     * `gameStateRestore` is forwarded verbatim by the passthrough list above.
     * This is the second half: if the server says we are owed a tally — a round
     * that scored while we were offline, which in an async game is the NORMAL way
     * a round ends, because the person who finishes it is never the person who
     * started it — replay it down the ordinary `round_ended` channel.
     *
     * Deliberately the SAME event the live game uses. GameScreen's phase machine
     * already knows how to take a `round_ended` and turn it into a cascade; it
     * needs no async branch to do it, and the tally Arslan sees in the morning is
     * therefore the identical tally Mom saw last night, chips and all — not a
     * summary, not a diff screen. `isReplay` on the payload is the only thing that
     * differs, and it exists so the cascade doesn't ask the server to advance a
     * round the server has already advanced.
     *
     * Queued to a microtask so the restore lands in GameContext FIRST — the
     * cascade needs the roster and the room before it can draw anybody's sheet.
     */
    socket.on('gameStateRestore', (data: GameStateRestorePayload) => {
      /**
       * WE ARE IN A ROOM. SAY SO — OR THROW AWAY THE TALLY.
       *
       * `inRoom()` gates `roundEnded`, `gameEnded` and friends, and it reads
       * `currentRoom`, which until now was ONLY ever set by `createRoom` and
       * `joinRoom`. Every live path goes through one of those, so the flag was
       * always incidentally true and nobody noticed it was load-bearing.
       *
       * The async resume path does not go through either. Mom opens her turn
       * hours later; the server finds her seat, joins her socket to the room and
       * replies `gameStateRestore` — she is, by every measure the SERVER has, in
       * the room. Her own client disagreed. So when she finished the round, the
       * `roundEnded` carrying the whole tally arrived on her socket and her
       * client dropped it on the floor, silently, and left her sitting on a
       * "waiting for the others" screen forever — waiting for a round that had
       * already been scored, in a game she had just completed.
       *
       * The server put us in the room. Believe it.
       */
      if (data?.active && data.roomCode) this.currentRoom = data.roomCode;

      if (!data?.pendingTally) return;
      const tally = data.pendingTally;
      queueMicrotask(() => this.emit('round_ended', tally));
    });

    // ONE registration. The old code registered `playerJoined` twice with
    // two different payload mappings — the second read `data.player`, which
    // the server does not always send, so it clobbered the roster with
    // `{ player: undefined }`.
    socket.on('playerJoined', (data: any) => {
      this.emit('playerJoined', data);
      if (data?.player) this.emit('player_joined', { player: data.player });
    });

    socket.on('playerLeft', (data: any) => {
      // Emit BOTH names: GameContext subscribes to `playerLeft` while older
      // screens subscribe to `player_left`. Previously only the snake_case
      // one was emitted, so GameContext's handler never ran.
      this.emit('playerLeft', data);
      this.emit('player_left', { playerId: data?.userId });
    });

    socket.on('player_left', (data: any) => {
      this.emit('player_left', { playerId: data?.playerId || data?.userId });
    });

    socket.on('player_ready', (data: any) => {
      const p = data?.player;
      if (p) {
        this.emit('playerReadyChanged', { userId: p.id, isReady: p.isReady });
        this.emit('player_ready', { playerId: p.id, isReady: !!p.isReady });
      }
    });

    socket.on('gameStarted', (data: any) => {
      if (!this.inRoom()) return;
      this.emit('game_started', data);
    });

    // The server says `gameAborted`; the app listens for `game_aborted`. An
    // event-name mismatch across a socket boundary fails SILENTLY — the host
    // calls the game off and every other player just sits in a dead room
    // forever, with nothing in any log to say why. Bridge it here, once.
    socket.on('gameAborted', (data: any) => {
      this.currentRoom = null; // the room is gone; ignore anything else from it
      this.emit('game_aborted', data || {});
    });

    socket.on('roundStarted', (data: any) => {
      if (!this.inRoom()) return;
      this.emit('round_started', {
        roundNumber: data.roundNumber,
        letter: data.letter,
        timeLimit: data.timeLimit,
        categories: data.categories,
        bans: data.bans || null,
        ladder: data.ladder || null,
      });
    });

    socket.on('playerSubmitted', (data: any) => {
      this.emit('answer_submitted', {
        playerId: data.userId || data.playerId,
        roundNumber: data.roundNumber || 1,
      });
    });

    socket.on('answer_submitted', (data: any) => {
      this.emit('answer_submitted', {
        playerId: data.playerId,
        roundNumber: data.roundNumber,
      });
    });

    socket.on('roundEnded', (data: any) => {
      if (!this.inRoom()) return;
      this.emit('round_ended', {
        roundNumber: data.roundNumber,
        letter: data.letter,
        scores: data.scores || {},
        playerTotalScores: data.playerTotalScores || {},
        submissions: data.submissions || {},
        answerDetails: data.answerDetails || {},
        bans: data.bans || null,
        ladder: data.ladder || null,
      });
    });

    socket.on('gameEnded', (data: any) => {
      // Dropped if we've already left. Quitting a solo game makes the server
      // end it and fire this at the socket on its way out — and the client
      // then navigated the quitter to a results screen for the game they had
      // just walked away from.
      if (!this.inRoom()) return;
      // Pass through the raw payload — it already has the shape consumers
      // expect ({ players: [...], winner, rounds }). Wrapping it as
      // `{ results: data }` was the cause of the "all final scores show 0"
      // bug. Both names are emitted for back-compat with the TV canvas.
      this.emit('game_ended', data);
      this.emit('gameEnded', data);
    });

    // Error handling — normalise ONCE, at the boundary.
    socket.on('error', (data: any) => {
      this.emit('error', normalizeWsError(data));
    });
  }

  // Request a rematch (host only)
  async requestRematch(): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) {
        reject(new Error('Not connected to server'));
        return;
      }

      const onStarted = () => {
        cleanup();
        resolve();
      };
      const onError = (raw: any) => {
        cleanup();
        reject(new Error(normalizeWsError(raw).message));
      };
      const cleanup = () => {
        clearTimeout(timer);
        socket.off('rematchStarted', onStarted);
        socket.off('error', onError);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Rematch request timeout'));
      }, 5000);

      socket.once('rematchStarted', onStarted);
      socket.once('error', onError);
      socket.emit('requestRematch');
    });
  }

  // Skip discussion time (host only)
  async skipDiscussion(): Promise<void> {
    this.socket?.emit('skipDiscussion', { roomCode: this.currentRoom });
  }

  // Typing status methods
  async setTypingStatus(isTyping: boolean, category?: string): Promise<void> {
    if (!this.socket) return;
    if (isTyping) this.socket.emit('player_typing', { category });
    else this.socket.emit('player_stopped_typing');
  }

  // Utility methods
  getCurrentRoom(): string | null {
    return this.currentRoom;
  }

  getCurrentUser(): Player | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /** Returns the uid of the currently-authenticated user, or null. */
  getAuthenticatedUserId(): string | null {
    return this.currentUser?.id || null;
  }

  /** Force-clear the current auth so the next authenticate() starts fresh. */
  resetAuth(): void {
    this.currentUser = null;
  }

  // Alias for getCurrentRoom for compatibility
  getRoomCode(): string | null {
    return this.currentRoom;
  }
}

// Export singleton instance
export const websocketService = new RealWebSocketService();
